import React, { useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import { Body1, Body2, Caption1, Dot, EditableText, SkeletonLoader } from "@activecollab/components";
import PropTypes from "prop-types";
import FormatDate from "~/components/Formatters/FormatDate";
import TaskListOptions from "~/components/task_list/TaskListOptions";
import { showErrorToast, showSuccessToast } from "~/components/Toast";
import UserLink from "~/components/UserLink/UserLink";
import { editTaskList, moveToTrash, reopenTaskList } from "~/store/actions/actions.taskLists";
import { getAllTaskListsByProjectId } from "~/store/selectors/selector.taskLists";
import { hasCollaborativePermissionsOnProject } from "~/store/selectors/selector.users";
import { classTypeIconResolver } from "~/utils/classTypeIconResolver";
import "./TrashArhiveItem.css";

const TasksArchiveItem = ({ item, loading }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const taskListMap = useSelector((state) => getAllTaskListsByProjectId(state, Number(item?.project_id)));

  const taskListName = useMemo(() => {
    if (taskListMap && item?.task_list_id) {
      return taskListMap.toJS()[item.task_list_id]?.name;
    }
  }, [item, taskListMap]);

  const itemIcon = useMemo(() => {
    return item?.class && classTypeIconResolver(item.class);
  }, [item]);

  const itemHashUrl = useMemo(() => {
    return `Task-${item?.id}-${item?.project_id}`;
  }, [item]);
  const canEditTaskLists = useSelector((state) =>
    hasCollaborativePermissionsOnProject(state, Number(item?.project_id))
  );

  const handleTaskClick = useCallback(
    (event) => {
      if (!event.altKey && !event.ctrlKey && !event.metaKey && !event.shiftKey) {
        event.preventDefault();
        searchParams.set("modal", itemHashUrl);
        setSearchParams(searchParams);
      }
    },
    [searchParams, setSearchParams, itemHashUrl]
  );

  const handleOnEdit = useCallback(() => {
    setTimeout(() => {
      inputRef?.current?.focus();
      inputRef?.current?.select();
    }, 0);
  }, [inputRef]);

  const handleOnRename = useCallback(
    (event) => {
      dispatch(editTaskList(item.project_id, item.id, { name: event.target.value })).then((action) => {
        const { response = {} } = action;
        const { result = {} } = response;
        const { single: taskListId } = result;

        if (taskListId) {
          showSuccessToast(t("Task list renamed"));
        } else {
          showErrorToast(t("Failed to update task list"));
        }
      });
    },
    [dispatch, item, t]
  );

  const handleOnReopen = useCallback(() => {
    dispatch(reopenTaskList(item.id));
  }, [dispatch, item]);

  const handleOnMoveToTrash = useCallback(() => {
    dispatch(moveToTrash(item.id));
  }, [dispatch, item]);

  if (loading) {
    return (
      <div className="tw-flex tw-gap-3">
        <SkeletonLoader
          style={{ width: "30px" }}
          data-testid="SkeletonLoader"
        />
        <div className="tw-flex tw-flex-col tw-gap-1 tw-w-full">
          <SkeletonLoader data-testid="SkeletonLoader" />
          <SkeletonLoader data-testid="SkeletonLoader" />
          <SkeletonLoader data-testid="SkeletonLoader" />
          <SkeletonLoader data-testid="SkeletonLoader" />
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className="tw-flex tw-gap-4"
        data-testid="trash-result"
      >
        <div className="tw-flex tw-flex-col tw-grow md:tw-flex-row tw-shrink-0 tw-max-w-full">
          <div className="tw-flex tw-items-center md:tw-self-start">
            <div className="tw-flex tw-items-center tw-w-10 md:tw-order-3">{itemIcon}</div>
            <Dot
              color="gray"
              size={5}
              className="tw-mx-3 tw-hidden md:tw-flex md:tw-order-2"
              data-testid="dot-component"
            />
            <>
              <Caption1
                color="secondary"
                weight="medium"
                data-testid="format-date"
                whitespace="no-wrap"
                className="md:tw-w-20"
                align="right"
              >
                <FormatDate
                  value={item.completed_on}
                  className="md:tw-order-1"
                />
              </Caption1>
            </>
          </div>
          <div className="tw-flex tw-flex-col tw-ml-10 md:tw-ml-0 md:tw-w-full tw-min-w-0">
            {item?.class === "Task" ? (
              <Link
                onClick={handleTaskClick}
                className="tw-no-underline tw-inline-flex"
                to={item.url_path}
              >
                <Body1
                  color="theme-primary"
                  weight="medium"
                  className="tw-break-all"
                >
                  {item.name}
                </Body1>
              </Link>
            ) : (
              <div className="tw-h-5 tw-mb-0.5 tw-flex tw-w-full tw-min-w-0 tw-items-center">
                <EditableText
                  value={item.name}
                  variant="Body 1"
                  weight="medium"
                  onSave={handleOnRename}
                  disabled={!canEditTaskLists}
                  inputProps={{ maxLength: 150 }}
                  ref={inputRef}
                  className="tw-truncate tw-align-middle -tw-ml-1"
                />
                {canEditTaskLists && (
                  <TaskListOptions
                    projectId={item.project_id}
                    id={item.id}
                    name={item.name}
                    isCompleted={true}
                    numberOfOpenTasks={item.open_tasks}
                    numberOfCompletedTasks={item.completed_tasks}
                    onEdit={handleOnEdit}
                    onReopen={handleOnReopen}
                    onMoveToTrash={handleOnMoveToTrash}
                    targetSize="small"
                    className="tw-shrink-0 options-target-menu-show"
                  />
                )}
              </div>
            )}
            {item?.class === "Task" && (
              <div className="tw-flex tw-gap-1">
                <Body2
                  color="secondary"
                  weight="medium"
                  className="tw-mt-1 tw-shrink-0"
                  data-testid="parent-type"
                >
                  {t("Task List")}:
                </Body2>
                <Body2
                  color="secondary"
                  weight="regular"
                  className="tw-mt-1 tw-break-all"
                  data-testid="parent-name"
                >
                  {taskListName || t("Unknown Task List")}
                </Body2>
              </div>
            )}
            <div className="tw-flex tw-gap-1">
              <Body2
                color="secondary"
                weight="medium"
                className="tw-mt-1"
                data-testid="parent-type"
              >
                {t("Completed By")}:
              </Body2>
              <Body2
                color="secondary"
                weight="regular"
                className="tw-mt-1 tw-break-all"
                data-testid="parent-name"
              >
                {item?.completed_by_id || item?.completed_by_name ? (
                  <UserLink
                    id={item?.completed_by_id}
                    email={item?.completed_by_email}
                    name={item?.completed_by_name}
                    className="completedByUserLink"
                    nameProps={{
                      color: "primary",
                      className: "tw-text-theme-700",
                    }}
                    size={16}
                    context="task-archive-item"
                    data-testid="user-link"
                  />
                ) : null}
              </Body2>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

TasksArchiveItem.displayName = "TasksArchiveItem";
TasksArchiveItem.propTypes = {
  item: PropTypes.object,
  loading: PropTypes.bool,
};

export default TasksArchiveItem;
